// C55 F10: tela de troca de senha obrigatoria no primeiro login (must_change_pwd).
// Backend POST /auth/password ja existia (apps/api/src/auth/password.ts) mas nao
// tinha UI: o operador ficava sem caminho pra trocar a senha inicial gerada pelo
// admin. Apos trocar com sucesso, o backend revoga todas as sessoes ativas, entao
// aqui fazemos logout + volta pro login com feedback claro.
import { useState, type FormEvent } from "react";
import { Navigate, useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { KeyRound } from "lucide-react";
import { useAuth } from "../contexts/AuthContext";
import { api, apiErrorMessage } from "../lib/api";
import { Button } from "../components/ui/button";
import { PasswordField } from "../components/ui/PasswordField";
import { ThemeToggle } from "../components/layout/ThemeToggle";

export function ChangePasswordPage(): JSX.Element {
  const { isAuthenticated, mustChangePassword, logout } = useAuth();
  const navigate = useNavigate();

  const [current, setCurrent] = useState("");
  const [newPwd, setNewPwd] = useState("");
  const [confirmPwd, setConfirmPwd] = useState("");
  const [submitting, setSubmitting] = useState(false);

  if (!isAuthenticated) {
    return <Navigate to="/login" replace />;
  }
  // Se o usuário chegou aqui sem must_change_pwd (ex: navegação manual), não bloqueia
  // o app: deixa ele seguir pra Home. A troca de senha continua disponível aqui.
  const isForced = mustChangePassword;

  const newPwdValid = newPwd.length >= 8;
  const confirmMatches = confirmPwd.length > 0 && confirmPwd === newPwd;
  const sameAsCurrent = current.length > 0 && newPwd.length > 0 && current === newPwd;

  const onSubmit = async (e: FormEvent): Promise<void> => {
    e.preventDefault();
    if (submitting) return;
    if (current.length === 0) {
      toast.error("Digite sua senha atual");
      return;
    }
    if (!newPwdValid) {
      toast.error("A nova senha precisa ter pelo menos 8 caracteres");
      return;
    }
    if (sameAsCurrent) {
      toast.error("A nova senha precisa ser diferente da atual");
      return;
    }
    if (!confirmMatches) {
      toast.error("A confirmação não bate com a nova senha");
      return;
    }
    setSubmitting(true);
    try {
      await api.post("/auth/password", { current, new: newPwd });
      toast.success("Senha alterada. Faça login novamente com a nova senha.");
      await logout();
      navigate("/login", { replace: true });
    } catch (err) {
      toast.error(apiErrorMessage(err));
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div
      style={{
        minHeight: "100vh",
        display: "flex",
        flexDirection: "column",
        background: "var(--color-bg)",
      }}
    >
      <header className="spread" style={{ padding: "1rem" }}>
        <span />
        <ThemeToggle />
      </header>
      <main
        className="stack"
        style={{
          flex: 1,
          alignItems: "center",
          justifyContent: "center",
          padding: "1rem",
          gap: "1.5rem",
        }}
      >
        <img src="/brand/hiit-logo-1080.png" alt="Hiit" style={{ width: "2.5rem", height: "2.5rem" }} />
        <div
          className="card"
          style={{
            width: "100%",
            maxWidth: "27.5rem",
            borderRadius: "var(--radius-xl)",
            boxShadow: "var(--shadow-card), inset 0 .3125rem 0 0 var(--color-lilac)",
          }}
        >
          <div className="cluster" style={{ gap: ".5rem" }}>
            <KeyRound className="icon" style={{ color: "var(--color-lilac)" }} aria-hidden />
            <h1 style={{ margin: 0, fontSize: "1.375rem", fontWeight: 900, letterSpacing: "-.02em" }}>
              {isForced ? "Troque sua senha para continuar" : "Trocar senha"}
            </h1>
          </div>
          <p style={{ margin: ".25rem 0 0", fontSize: ".8125rem", fontWeight: 600, color: "var(--color-ink-2)" }}>
            {isForced
              ? "Esta é sua primeira vez aqui. Por segurança, defina uma senha só sua antes de continuar."
              : "Defina uma nova senha de acesso."}
          </p>
          <form onSubmit={onSubmit} className="stack" style={{ marginTop: "1rem" }} noValidate>
            <label className="field" htmlFor="current-pwd">
              Senha atual
              <PasswordField
                id="current-pwd"
                value={current}
                onChange={(e) => setCurrent(e.target.value)}
                autoComplete="current-password"
                autoFocus
                required
                disabled={submitting}
              />
            </label>
            <label className="field" htmlFor="new-pwd">
              Nova senha (mínimo 8 caracteres)
              <PasswordField
                id="new-pwd"
                value={newPwd}
                onChange={(e) => setNewPwd(e.target.value)}
                autoComplete="new-password"
                minLength={8}
                required
                disabled={submitting}
              />
              {newPwd.length > 0 && !newPwdValid ? (
                <span className="field__hint" style={{ color: "var(--color-bad)" }}>
                  A senha precisa ter pelo menos 8 caracteres.
                </span>
              ) : null}
              {sameAsCurrent ? (
                <span className="field__hint" style={{ color: "var(--color-bad)" }}>
                  A nova senha precisa ser diferente da atual.
                </span>
              ) : null}
            </label>
            <label className="field" htmlFor="confirm-pwd">
              Confirme a nova senha
              <PasswordField
                id="confirm-pwd"
                value={confirmPwd}
                onChange={(e) => setConfirmPwd(e.target.value)}
                autoComplete="new-password"
                required
                disabled={submitting}
              />
              {confirmPwd.length > 0 && !confirmMatches ? (
                <span className="field__hint" style={{ color: "var(--color-bad)" }}>
                  As senhas não são iguais.
                </span>
              ) : null}
            </label>
            <Button type="submit" loading={submitting} style={{ width: "100%" }}>
              Salvar nova senha
            </Button>
            {!isForced ? (
              <button
                type="button"
                onClick={() => navigate(-1)}
                style={{
                  width: "100%",
                  textAlign: "center",
                  fontSize: ".75rem",
                  fontWeight: 700,
                  color: "var(--color-ink-3)",
                  background: "transparent",
                  border: 0,
                  cursor: "pointer",
                }}
              >
                Cancelar e voltar
              </button>
            ) : null}
          </form>
        </div>
        <div style={{ fontSize: ".6875rem", fontWeight: 700, color: "var(--color-ink-3)" }}>
          Vitrine Hiit · v1.0.1
        </div>
      </main>
    </div>
  );
}
